// Profile.js
import React, { useState, useEffect, useRef } from 'react';
import { auth, db, storage } from './firebase-config';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import LoadingSpinner from './LoadingSpinner';
import { signOut } from 'firebase/auth';
import LetterAvatar from './LetterAvatar';
import LinkedInBadge from './LinkedInBadge';
import { FaPencilAlt } from 'react-icons/fa';
import Modal from 'react-modal';

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [activeApplicants, setActiveApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    fullName: '',
    title: '',
    location: '',
    phone: '',
    about: '',
    experience: '',
    education: '',
    skills: '',
    email: '',
    website: '',
    companySize: '',
    founded: '',
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [resumeUploadStatus, setResumeUploadStatus] = useState(null);
  const [workExperience, setWorkExperience] = useState([]);
  const [portfolioLinks, setPortfolioLinks] = useState([]);
  const [newPortfolioUrl, setNewPortfolioUrl] = useState('');
  const [newPortfolioTitle, setNewPortfolioTitle] = useState('');
  const [portfolioPreview, setPortfolioPreview] = useState(null);
  const [portfolioError, setPortfolioError] = useState('');
  const [isSavingLinkedin, setIsSavingLinkedin] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [isSavingSection, setIsSavingSection] = useState(false);
  const [showResumeLinkModal, setShowResumeLinkModal] = useState(false);
  const [resumeLinkInput, setResumeLinkInput] = useState('');
  const [linkedinInput, setLinkedinInput] = useState('');

  useEffect(() => {
    let isMounted = true;
    
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) {
        if (isMounted) {
          setLoading(false);
          navigate('/login');
        }
        return;
      }

      try {
        // Check if user is employee or employer
        const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
        const employerDoc = await getDoc(doc(db, 'employers', user.uid));
        
        if (!isMounted) return;

        if (employeeDoc.exists()) {
          const data = employeeDoc.data();
          setUserData({ ...data, id: user.uid });
          setUserRole('employee');
          setAccountType('employee');
          
          // Handle work experience - check both workExperience and experience fields
          let workExpData = data.workExperience || [];
          if (!workExpData.length && data.experience) {
            // Convert old experience format to workExperience format
            const experienceLines = Array.isArray(data.experience) ? data.experience : [data.experience];
            workExpData = experienceLines.map(line => {
              if (typeof line === 'string') {
                const parts = line.split('|').map(s => s.trim());
                if (parts.length >= 3) {
                  const [title, company, dates] = parts;
                  const [start, end] = dates.split('-').map(s => s.trim());
                  return { title, company, start, end };
                } else {
                  return { title: line, company: '', start: '', end: '' };
                }
              }
              return line;
            });
          }
          setWorkExperience(workExpData);
          
          setPortfolioLinks(data.portfolioLinks || []);

          // Safely handle potentially undefined or non-array values
          const experienceData = data.experience || [];
          const educationData = data.education || [];
          const skillsData = data.skills || [];

          setEditedData({
            fullName: data.fullName || '',
            title: data.title || '',
            location: data.location || '',
            phone: data.phone || '',
            about: data.about || '',
            experience: Array.isArray(experienceData) ? experienceData.join('\n') : String(experienceData),
            education: Array.isArray(educationData) ? educationData.join('\n') : String(educationData),
            skills: Array.isArray(skillsData) ? skillsData.join(', ') : String(skillsData),
            email: data.email || '',
          });

          // Fetch applications for employee
          const applicationsQuery = query(
            collection(db, 'applications'),
            where('employeeId', '==', user.uid)
          );
          const applicationsSnapshot = await getDocs(applicationsQuery);
          const applicationsList = applicationsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setApplications(applicationsList);
        } else if (employerDoc.exists()) {
          const data = employerDoc.data();
          setUserData({ ...data, id: user.uid });
          setUserRole('employer');
          setAccountType('employer');
          setEditedData({
            fullName: data.companyName || '',
            title: data.industry || '',
            location: data.location || '',
            phone: data.phone || '',
            about: data.description || '',
            website: data.website || '',
            companySize: data.companySize || '',
            founded: data.founded || '',
            email: data.email || '',
          });
        } else {
          setError('No user profile found');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        if (isMounted) {
          setError('Error loading profile');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleResumeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setResumeUploadStatus({ type: 'info', message: 'Uploading resume...' });
      const user = auth.currentUser;
      const resumeRef = ref(storage, `resumes/${user.uid}/${file.name}`);
      await uploadBytes(resumeRef, file);
      const downloadURL = await getDownloadURL(resumeRef);

      // Update user document with resume URL
      const userDocRef = doc(db, 'employees', user.uid);
      await updateDoc(userDocRef, {
        resumeURL: downloadURL,
        resumeName: file.name
      });

      setUserData(prev => ({
        ...prev,
        resumeURL: downloadURL,
        resumeName: file.name
      }));

      setResumeUploadStatus({ type: 'success', message: 'Resume uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading resume:', error);
      setResumeUploadStatus({ type: 'error', message: 'Failed to upload resume. Please try again.' });
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset error state
    setImageUploadError('');

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setImageUploadError('Please upload a valid image file (JPEG, PNG)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setImageUploadError('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }

      const imageRef = ref(storage, `profile-pictures/${user.uid}/${file.name}`);
      
      // Upload the file
      const uploadResult = await uploadBytes(imageRef, file);
      if (!uploadResult) {
        throw new Error('Failed to upload image');
      }

      // Get the download URL
      const downloadURL = await getDownloadURL(imageRef);
      if (!downloadURL) {
        throw new Error('Failed to get image URL');
      }
      
      // Update the user's document in Firestore
      const userRef = doc(db, userRole === 'employee' ? 'employees' : 'employers', user.uid);
      await updateDoc(userRef, {
        profilePic: downloadURL
      });

      // Update local state
      setUserData(prev => ({ ...prev, profilePic: downloadURL }));
      setEditedData(prev => ({ ...prev, profilePic: downloadURL }));
    } catch (error) {
      console.error('Error uploading image:', error);
      setImageUploadError(error.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return;

    setLoading(true);
    try {
      const collectionName = userRole === 'employee' ? 'employees' : 'employers';
      const userRef = doc(db, collectionName, user.uid);

      let dataToUpdate = { ...editedData };
      
      if (userRole === 'employee') {
        if (typeof dataToUpdate.experience === 'string') {
          // Convert experience string to workExperience objects
          const experienceLines = dataToUpdate.experience.split('\n').filter(item => item && item.trim() !== '');
          const workExpObjects = experienceLines.map(line => {
            const parts = line.split('|').map(s => s.trim());
            if (parts.length >= 3) {
              const [title, company, dates] = parts;
              const [start, end] = dates.split('-').map(s => s.trim());
              return { title, company, start, end };
            } else {
              // Fallback for single line format
              return { title: line, company: '', start: '', end: '' };
            }
          });
          dataToUpdate.workExperience = workExpObjects;
          delete dataToUpdate.experience; // Remove the old experience field
        }
        if (typeof dataToUpdate.education === 'string') {
          dataToUpdate.education = dataToUpdate.education.split('\n').filter(item => item && item.trim() !== '');
        }
        if (typeof dataToUpdate.skills === 'string') {
          dataToUpdate.skills = dataToUpdate.skills.split(',').map(skill => skill.trim()).filter(Boolean);
        }

        dataToUpdate = {
          fullName: dataToUpdate.fullName,
          title: dataToUpdate.title,
          location: dataToUpdate.location,
          phone: dataToUpdate.phone,
          about: dataToUpdate.about,
          workExperience: dataToUpdate.workExperience,
          education: dataToUpdate.education,
          skills: dataToUpdate.skills,
          email: dataToUpdate.email,
        };
      } else {
        dataToUpdate = {
          companyName: dataToUpdate.fullName,
          industry: dataToUpdate.title,
          location: dataToUpdate.location,
          phone: dataToUpdate.phone,
          description: dataToUpdate.about,
          website: dataToUpdate.website,
          companySize: dataToUpdate.companySize,
          founded: dataToUpdate.founded,
          email: dataToUpdate.email,
        };
      }

      await updateDoc(userRef, dataToUpdate);
      setUserData(prev => ({ ...prev, ...dataToUpdate }));
      // Update workExperience state if it was changed
      if (dataToUpdate.workExperience) {
        setWorkExperience(dataToUpdate.workExperience);
      }
      setIsEditing(false);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleEditBio = () => {
    setIsEditingBio(true);
  };

  const handleSaveBio = async () => {
    if (bioText.length > 300) {
      alert('Bio must be 300 characters or less');
      return;
    }

    setIsSavingBio(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const docRef = doc(db, 'employees', user.uid);
        await updateDoc(docRef, {
          bio: bioText
        });
        setUserData(prev => ({ ...prev, bio: bioText }));
      }
    } catch (error) {
      console.error('Error saving bio:', error);
    } finally {
      setIsEditingBio(false);
      setIsSavingBio(false);
    }
  };

  const handleCancelEdit = () => {
    setBioText(userData?.bio || '');
    setIsEditingBio(false);
  };

  const handleEdit = () => {
    setEditedData(userData);
    setIsEditing(true);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  };

  const handleAddPortfolio = async () => {
    setPortfolioError('');
    if (!newPortfolioUrl || !newPortfolioTitle) {
      setPortfolioError('Please enter both a title and a URL.');
      return;
    }
    // Optionally, fetch a preview image using a public API (e.g., microlink.io)
    let image = '';
    try {
      const res = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(newPortfolioUrl)}`);
      const data = await res.json();
      image = data?.data?.screenshot?.url || data?.data?.image?.url || '';
    } catch (e) {
      // Ignore preview error
    }
    const newLink = { title: newPortfolioTitle, url: newPortfolioUrl, image };
    const updatedLinks = [...portfolioLinks, newLink];
    setPortfolioLinks(updatedLinks);
    setNewPortfolioUrl('');
    setNewPortfolioTitle('');
    // Save to Firestore
    try {
      const user = auth.currentUser;
      if (user) {
        await updateDoc(doc(db, 'employees', user.uid), { portfolioLinks: updatedLinks });
      }
    } catch (e) {
      setPortfolioError('Failed to save portfolio link.');
    }
  };

  // Save LinkedIn URL to Firestore and local state
  const handleEditLinkedinUrl = async (newUrl) => {
    if (!userData?.id) return;
    setIsSavingLinkedin(true);
    try {
      // Fetch LinkedIn public data
      const res = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(newUrl)}`);
      const data = await res.json();
      const linkedinData = {
        avatar: data?.data?.image?.url || '',
        headline: data?.data?.title || '',
      };
      await updateDoc(doc(db, 'employees', userData.id), { linkedin: newUrl, linkedinData });
      setUserData(prev => ({ ...prev, linkedin: newUrl, linkedinData }));
    } catch (e) {
      alert('Failed to fetch LinkedIn data.');
    } finally {
      setIsSavingLinkedin(false);
    }
  };

  // Handler for opening section edit modal
  const handleSectionEdit = (section) => {
    setEditingSection(section);
    if (section === 'workExperience') {
      setEditingValue(
        workExperience.map(exp => `${exp.title || ''} | ${exp.company || ''} | ${exp.start || ''} - ${exp.end || ''}`).join('\n')
      );
    }
    // Add more sections as needed
  };

  // Handler for saving section
  const handleSaveSection = async () => {
    setIsSavingSection(true);
    try {
      if (editingSection === 'workExperience') {
        // Parse lines into work experience objects
        const newWorkExp = editingValue.split('\n').map(line => {
          const [title = '', company = '', dates = ''] = line.split('|').map(s => s.trim());
          const [start = '', end = ''] = dates.split('-').map(s => s.trim());
          return { title, company, start, end };
        }).filter(exp => exp.title || exp.company);
        // Update Firestore
        const user = auth.currentUser;
        if (user) {
          await updateDoc(doc(db, 'employees', user.uid), { workExperience: newWorkExp });
        }
        setWorkExperience(newWorkExp);
      }
      // Add more sections as needed
      setEditingSection(null);
    } catch (e) {
      alert('Failed to save.');
    } finally {
      setIsSavingSection(false);
    }
  };

  const handleSaveResumeLink = async () => {
    if (!resumeLinkInput) {
      setError('Please enter a valid resume link');
      return;
    }

    try {
      setResumeUploadStatus({ type: 'info', message: 'Processing resume link...' });
      const user = auth.currentUser;
      if (user) {
        await updateDoc(doc(db, 'employees', user.uid), {
          resumeURL: resumeLinkInput,
          resumeName: 'Linked Resume'
        });
        setUserData(prev => ({
          ...prev,
          resumeURL: resumeLinkInput,
          resumeName: 'Linked Resume'
        }));
        setResumeUploadStatus({ type: 'success', message: 'Resume linked successfully!' });
      }
    } catch (error) {
      console.error('Error saving resume link:', error);
      setResumeUploadStatus({ type: 'error', message: 'Failed to link resume. Please try again.' });
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="error-message">
          <p>No profile data found</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-content">
        {/* Only show the old profile header for employers */}
        {userRole === 'employer' && (
          <div className="profile-header">
            <div className="profile-picture-container">
              {userData?.profilePic && userData.profilePic !== 'https://via.placeholder.com/40' ? (
                <img
                  src={userData.profilePic}
                  alt="Profile"
                  className="profile-picture"
                />
              ) : (
                <LetterAvatar
                  name={userData?.fullName || userData?.companyName || 'Unknown'}
                  size={120}
                  className="profile-picture"
                />
              )}
              <label htmlFor="profile-image-upload" className="upload-button">
                <span>Change Photo</span>
                <input
                  type="file"
                  id="profile-image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            <div className="profile-info">
              <h1 className="profile-name">
                {userRole === 'employer' ? userData?.companyName : userData?.fullName}
              </h1>
              <p className="profile-title">
                {userRole === 'employer' ? userData?.industry : userData?.title || 'Professional'}
              </p>
              <p className="profile-location">{userData?.location || 'Location not specified'}</p>
            </div>
            {!isEditing && (
              <div className="profile-actions">
                <button onClick={handleEdit} className="edit-button">
                  Edit Profile
                </button>
                {accountType === 'employer' && (
                  <button 
                    onClick={() => navigate('/applicants')} 
                    className="view-applicants-button"
                  >
                    View Active Applicants
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {!isEditing ? (
          <div className="profile-details">
            {userRole === 'employee' ? (
              <>
                {/* Profile Picture and Name/Title Box */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, marginBottom: 18 }}>
                  {userData?.profilePic && userData.profilePic !== 'https://via.placeholder.com/40' ? (
                    <img src={userData.profilePic} alt="Profile" className="job-logo-square" style={{ width: 90, height: 90, borderRadius: 16, objectFit: 'cover', marginBottom: 10 }} />
                  ) : (
                    <LetterAvatar name={userData?.fullName || 'Unknown'} size={90} className="job-logo-square" style={{ marginBottom: 10 }} />
                  )}
                  <div>
                    <div className="profile-jobcard-title" style={{ textAlign: 'center' }}>{userData?.fullName}</div>
                    <div className="profile-jobcard-subtitle" style={{ textAlign: 'center' }}>{userData?.title || 'Professional'}</div>
                  </div>
                </div>
                {/* Contact Info Box */}
                <div className="profile-jobcard-info-box">
                  <div className="profile-jobcard-section-title">Contact Information</div>
                  <div>Email: <span className="profile-jobcard-value">{userData?.email || 'Not provided'}</span></div>
                  {userData?.phone && <div>Phone: <span className="profile-jobcard-value">{userData.phone}</span></div>}
                </div>
                {/* Work Experience Box */}
                <div className="profile-jobcard-info-box work-experience-section">
                  <div className="profile-jobcard-section-title">Work Experience</div>
                  {workExperience.length > 0 ? workExperience.map((exp, idx) => (
                    <div key={idx} className="profile-jobcard-value">{exp.title}</div>
                  )) : (
                    <div className="profile-jobcard-value">No work experience added yet.</div>
                  )}
                </div>
                {/* Education Box */}
                <div className="profile-jobcard-info-box">
                  <div className="profile-jobcard-section-title">Education</div>
                  {Array.isArray(userData?.education) && userData.education.length > 0 ? (
                    userData.education.map((ed, idx) => (
                      <div key={idx} className="profile-jobcard-value">{ed}</div>
                    ))
                  ) : (
                    <div className="profile-jobcard-value">No education added yet.</div>
                  )}
                </div>
                {/* Skills Box */}
                <div className="profile-jobcard-info-box skills-section">
                  <div className="profile-jobcard-section-title">Skills</div>
                  {Array.isArray(userData?.skills) && userData.skills.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {userData.skills.map((skill, idx) => (
                        <span key={idx} className="profile-jobcard-skill-tag">{skill}</span>
                      ))}
                    </div>
                  ) : (
                    <div className="profile-jobcard-value">No skills listed yet.</div>
                  )}
                </div>
                {/* Portfolio Box */}
                <div className="profile-jobcard-info-box">
                  <div className="profile-jobcard-section-title">Portfolio</div>
                  {portfolioLinks.length > 0 ? portfolioLinks.map((item, idx) => (
                    <a className="profile-jobcard-value" key={idx} href={item.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: 6, color: '#D65A31', fontWeight: 700 }}>{item.title}</a>
                  )) : (
                    <div className="profile-jobcard-value">No portfolio links added yet.</div>
                  )}
                </div>

                {/* LinkedIn Section */}
                <div className="profile-jobcard-info-box">
                  <div className="profile-jobcard-section-title">LinkedIn</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {userData?.linkedinUrl && (
                      <a href={userData.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0077B5', fontWeight: 700, marginBottom: 8 }}>
                        {userData.linkedinUrl}
                      </a>
                    )}
                    <input
                      type="url"
                      placeholder="Paste LinkedIn profile URL"
                      value={linkedinInput}
                      onChange={e => setLinkedinInput(e.target.value)}
                      className="edit-input"
                      style={{ marginBottom: 8 }}
                    />
                    <button
                      className="profile-action-btn"
                      style={{ width: '100%', height: 40, background: '#0077B5', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
                      onClick={async () => {
                        setIsSavingLinkedin(true);
                        try {
                          const user = auth.currentUser;
                          if (user) {
                            await updateDoc(doc(db, 'employees', user.uid), { linkedinUrl: linkedinInput });
                            setUserData(prev => ({ ...prev, linkedinUrl: linkedinInput }));
                          }
                        } catch (e) {
                          alert('Failed to save LinkedIn link.');
                        } finally {
                          setIsSavingLinkedin(false);
                        }
                      }}
                      disabled={isSavingLinkedin || !linkedinInput}
                    >
                      {isSavingLinkedin ? 'Saving...' : (userData?.linkedinUrl ? 'Update LinkedIn Link' : 'Save LinkedIn Link')}
                    </button>
                  </div>
                </div>

                {/* Resume Section */}
                <div className="profile-jobcard-info-box">
                  <div className="profile-jobcard-section-title">Resume</div>
                  {userData?.resumeURL || userData?.resumeUrl ? (
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: '#22223B' }}>
                      {userData.resumeName ? userData.resumeName : 'Resume Uploaded'}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
                      No Resume Added
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
                    <button
                      className="profile-action-btn"
                      style={{ width: '100%', height: 48, background: '#4267B2', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 18, cursor: 'pointer', margin: 0 }}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                      Upload from Device
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleResumeUpload}
                      accept=".pdf,.doc,.docx"
                      style={{ display: 'none' }}
                    />
                    <button
                      className="profile-action-btn"
                      style={{ width: '100%', height: 48, background: '#D65A31', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 18, cursor: 'pointer', margin: 0 }}
                      onClick={() => setShowResumeLinkModal(true)}
                    >
                      Paste Resume Link
                    </button>
                  </div>
                  {userData?.resumeURL && (
                    <a 
                      href={userData.resumeURL} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="view-resume-button"
                      style={{ marginTop: 12 }}
                    >
                      View Current Resume
                    </a>
                  )}
                  {resumeUploadStatus && (
                    <p className={`upload-status ${resumeUploadStatus.type}`}>{resumeUploadStatus.message}</p>
                  )}
                </div>

                {/* Edit Profile & Sign Out Buttons */}
                <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  <button
                    className="edit-profile-btn"
                    style={{ flex: 1, minWidth: 140, background: '#22223B', color: '#FAF3E0', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 16, padding: '14px 0', cursor: 'pointer' }}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </button>
                  <button
                    className="sign-out-btn"
                    style={{ flex: 1, minWidth: 140, background: '#ff4757', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 16, padding: '14px 0', cursor: 'pointer' }}
                    onClick={handleSignOut}
                  >
                    Sign Out
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="detail-group">
                  <h3>Company Details</h3>
                  <p>Size: {userData?.companySize || 'Not specified'}</p>
                  <p>Founded: {userData?.founded || 'Not specified'}</p>
                </div>

                <div className="detail-group">
                  <h3>About Company</h3>
                  <p>{userData?.description || 'No company description available.'}</p>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="edit-profile-screen">


            <div className="edit-profile-content">
              <div className="edit-form">
                <div className="form-group">
                  <label>{userRole === 'employer' ? 'Company Name' : 'Full Name'}</label>
                  <input
                    type="text"
                    name="fullName"
                    value={editedData.fullName}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                </div>

                <div className="form-group">
                  <label>{userRole === 'employer' ? 'Industry' : 'Title'}</label>
                  <input
                    type="text"
                    name="title"
                    value={editedData.title}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={editedData.location}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={editedData.phone}
                    onChange={handleInputChange}
                    className="edit-input"
                  />
                </div>

                {userRole === 'employee' ? (
                  <>
                    <div className="form-group">
                      <label>Experience (one per line)</label>
                      <textarea
                        name="experience"
                        value={editedData.experience}
                        onChange={handleInputChange}
                        className="edit-textarea"
                        placeholder="Enter each experience on a new line"
                      />
                    </div>

                    <div className="form-group">
                      <label>Education (one per line)</label>
                      <textarea
                        name="education"
                        value={editedData.education}
                        onChange={handleInputChange}
                        className="edit-textarea"
                        placeholder="Enter each education entry on a new line"
                      />
                    </div>

                    <div className="form-group">
                      <label>Skills (comma-separated)</label>
                      <input
                        type="text"
                        name="skills"
                        value={editedData.skills}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="e.g., JavaScript, React, Node.js"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Website</label>
                      <input
                        type="url"
                        name="website"
                        value={editedData.website}
                        onChange={handleInputChange}
                        className="edit-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Company Size</label>
                      <input
                        type="text"
                        name="companySize"
                        value={editedData.companySize}
                        onChange={handleInputChange}
                        className="edit-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Founded Year</label>
                      <input
                        type="text"
                        name="founded"
                        value={editedData.founded}
                        onChange={handleInputChange}
                        className="edit-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>About Company</label>
                      <textarea
                        name="about"
                        value={editedData.about}
                        onChange={handleInputChange}
                        className="edit-textarea"
                      />
                    </div>
                  </>
                )}

                <div className="edit-buttons">
                  <button type="button" className="save-btn" onClick={handleSave}>
                    Save Changes
                  </button>
                  <button type="button" className="cancel-btn" onClick={() => setIsEditing(false)}>
                    Cancel
                  </button>
                  {imageUploadError && (
                    <div className="error-message">
                      {imageUploadError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showSuccess && (
          <div className="save-status success">
            Profile Updated!
          </div>
        )}
      </div>

      {showResumeLinkModal && (
        <div className="modal-overlay" onClick={() => setShowResumeLinkModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, padding: 32 }}>
            <h3>Paste Resume Link</h3>
            <input
              type="url"
              className="edit-input"
              placeholder="Paste a link to your resume (Google Drive, Dropbox, etc.)"
              value={resumeLinkInput}
              onChange={e => setResumeLinkInput(e.target.value)}
              style={{ marginBottom: 18 }}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <button className="save-btn" onClick={handleSaveResumeLink}>Save</button>
              <button className="cancel-btn" onClick={() => setShowResumeLinkModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
